// Fleather/Parchment (Quill-style) delta JSON → HTML for product/service
// descriptions on the public invoice/proposal/estimate viewers.
//
// Product-catalog descriptions are authored in the Flutter app with Fleather
// and stored as delta JSON strings, e.g.
// `[{"insert":"bold","attributes":{"b":true}},{"insert":"\n"}]`.
// Legacy descriptions are plain text. This module renders either form:
// deltas become a whitelisted HTML subset, anything else is escaped
// verbatim (containers keep `white-space: pre-wrap` for those newlines).
//
// Supported (the catalog editor's FleatherToolbar.basic set): inline
// `b`/`i`/`u`/`s` + `a` links, line-level `heading` 1–3, `block` ul/ol/cl,
// and `alignment`. Everything else — colors, indent, embeds — is ignored,
// never leaked as raw markup. All text is HTML-escaped and link protocols
// are whitelisted (http, https, mailto, tel), so the output is safe for
// `v-html`. Web links open in a new tab with `rel="noopener noreferrer
// nofollow"`; mailto: and tel: links open the mail or phone app in place.
//
// The booking page's About (contract 2026-10-02) renders through here too,
// with `headingOffset` so the author's headings nest under the page's own.
//
// Mirrors functions-pdf/src/pdfShared/richText.ts (invoice PDFs) and
// lib/services/quill_to_html.dart (Flutter app) so every surface renders
// deltas consistently. The notes viewer has its own richer converter
// (server/utils/noteDelta.ts) — descriptions deliberately support only
// the small toolbar subset above.

interface DeltaOp {
  insert?: unknown
  attributes?: Record<string, unknown>
}

interface Span {
  text: string
  attrs: Record<string, unknown> | null
}

interface Line {
  spans: Span[]
  /** Line-level attributes taken from the op that carries the "\n". */
  attrs: Record<string, unknown> | null
}

const escapeHtml = (raw: string): string =>
  raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

/** Returns the parsed delta ops, or null when `source` isn't a delta. */
const tryParseDeltaOps = (source: string): DeltaOp[] | null => {
  const trimmed = source.trim()
  // Cheap pre-check: a delta is always a JSON array of objects.
  if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  }
  catch {
    return null
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null
  const isOp = (op: unknown): op is DeltaOp =>
    typeof op === 'object' && op !== null && 'insert' in op
  return parsed.every(isOp) ? (parsed as DeltaOp[]) : null
}

const lineHasBlock = (line: Line): boolean =>
  typeof line.attrs?.block === 'string'

/** Split ops into lines; each line ends at a "\n" whose op carries the
 *  line-level attributes (heading/block/alignment) for that line. */
const splitLines = (ops: DeltaOp[]): Line[] => {
  const lines: Line[] = []
  let current: Line = { spans: [], attrs: null }
  for (const op of ops) {
    if (typeof op.insert !== 'string') continue // ignore embeds
    const attrs = op.attributes ?? null
    let chunk = op.insert
    while (chunk.includes('\n')) {
      const idx = chunk.indexOf('\n')
      if (idx > 0) current.spans.push({ text: chunk.slice(0, idx), attrs })
      current.attrs = attrs
      lines.push(current)
      current = { spans: [], attrs: null }
      chunk = chunk.slice(idx + 1)
    }
    if (chunk.length > 0) current.spans.push({ text: chunk, attrs })
  }
  if (current.spans.length > 0) lines.push(current)
  // Quill's invariant is a trailing "\n"; don't render trailing blanks.
  for (;;) {
    const last = lines.at(-1)
    if (!last || last.spans.length > 0 || lineHasBlock(last)) break
    lines.pop()
  }
  return lines
}

const SAFE_HREF = /^(?:https?:|mailto:|tel:)/i
const WEB_HREF = /^https?:/i

const renderSpan = (span: Span): string => {
  let out = escapeHtml(span.text)
  const attrs = span.attrs
  if (!attrs) return out
  const href = typeof attrs.a === 'string' ? attrs.a.trim() : ''
  if (SAFE_HREF.test(href)) {
    out = WEB_HREF.test(href)
      ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer nofollow">${out}</a>`
      : `<a href="${escapeHtml(href)}">${out}</a>`
  }
  if (attrs.u === true) out = `<u>${out}</u>`
  if (attrs.s === true) out = `<del>${out}</del>`
  if (attrs.i === true) out = `<em>${out}</em>`
  if (attrs.b === true) out = `<strong>${out}</strong>`
  return out
}

const ALIGNMENTS = new Set(['center', 'right', 'justify'])

const alignStyle = (line: Line): string => {
  const alignment = line.attrs?.alignment
  if (typeof alignment === 'string' && ALIGNMENTS.has(alignment)) {
    return ` style="text-align:${alignment}"`
  }
  return ''
}

const deltaToHtml = (ops: DeltaOp[], headingOffset: number): string => {
  const lines = splitLines(ops)
  if (lines.length === 0) return ''
  const out: string[] = []
  let openList: 'ul' | 'ol' | null = null
  const closeList = (): void => {
    if (openList) {
      out.push(`</${openList}>`)
      openList = null
    }
  }
  for (const line of lines) {
    const inner = line.spans.map(renderSpan).join('')
    const block = line.attrs?.block
    // Checklists ("cl") render as plain bullets — checkbox state has no
    // meaning on a read-only public page.
    const listTag = block === 'ul' || block === 'cl' ? 'ul' : block === 'ol' ? 'ol' : null
    if (listTag) {
      if (openList !== listTag) {
        closeList()
        out.push(`<${listTag}>`)
        openList = listTag
      }
      out.push(`<li${alignStyle(line)}>${inner}</li>`)
      continue
    }
    closeList()
    const heading = line.attrs?.heading
    const tag = heading === 1 || heading === 2 || heading === 3
      ? `h${Math.min(6, heading + headingOffset)}`
      : 'p'
    out.push(`<${tag}${alignStyle(line)}>${inner}</${tag}>`)
  }
  closeList()
  return out.join('')
}

export interface RichDescriptionOptions {
  /**
   * Added to every heading level, capped at h6: 2 renders the author's
   * heading 1 as an `<h3>` under a section's own `<h2>`. Default 0.
   */
  headingOffset?: number
}

/**
 * Renders a product/service description for `v-html`. Delta JSON becomes
 * a safe rich-HTML subset; anything else is HTML-escaped plain text.
 */
export const richDescriptionToHtml = (source: string | null | undefined, options: RichDescriptionOptions = {}): string => {
  if (!source) return ''
  const ops = tryParseDeltaOps(source)
  if (ops === null) return escapeHtml(source)
  const offset = Number.isInteger(options.headingOffset) ? Math.min(5, Math.max(0, options.headingOffset!)) : 0
  return deltaToHtml(ops, offset)
}

/**
 * True when a description has no visible content in either format — an
 * empty Fleather document still serializes as `[{"insert":"\n"}]`, which
 * is a non-empty string.
 */
export const richDescriptionIsEmpty = (source: string | null | undefined): boolean => {
  if (!source || source.trim().length === 0) return true
  const ops = tryParseDeltaOps(source)
  if (ops === null) return false
  return ops.every(
    op => typeof op.insert !== 'string' || op.insert.trim().length === 0,
  )
}
